import { DesignMode, EventOrder } from "@amzn/rttv_web_app/lib/shared/constants";
import { validRegions, validThemes } from "../../config/constants";

/** Details of a shipment including tracking info and events */
export interface ShipmentDetails {
    trackingId : string,
    carrierId : string,
    trackingRequestId : string,
    currentStatus : string,
    edd : number,
    shipmentEvents : Array<ShipmentEvent>,
    trackingEstimates?: Array<TrackingEstimate>,
}

/** Individual event in a shipment's tracking history */
export interface ShipmentEvent {
    eventId : string,
    eventStatus : string,
    eventShortDescription : string,
    eventDescription? : string,
    eventAddress? : string,
    eventTime : number
}
/** A supply-chain estimate returned by the RTTV tracking service */
export interface TrackingEstimate {
    type: string,
    estimatedInterval: {
        startTime: string,
        endTime: string
    },
    lastUpdatedTime: string
}

/** Input format for client-provided tracking events */
export interface ClientEventInput {
    eventCode: string,
    eventDescription: string
}

/**
 * Valid identifier types for the GetTrackingResponsesForSupplyChain API.
 * These map to IndexableAttribute keys in the RTTV backend.
 */
export const VALID_IDENTIFIER_TYPES = [
    "CARRIER_TRACKING_NUMBER",
    "AMAZON_FULFILLMENT_TRACKING_NUMBER",
    "CONTAINER_ID",
    "AMAZON_REFERENCE_NUMBER",
    "ACSIN"
] as const;

/** Configuration options for initializing the tracking widget */
export interface ClientInput{
    theme?: typeof validThemes[number],
    apikey?: string,
    authorization?: string,
    tenantId?: string,
    dataSourceConfigurationId: string,
    trackingId?: string,
    carrierId?: string,
    region?: typeof validRegions[number],
    logisticsMode?: string,
    designMode?: string,
    displayHorizontalProgressBar?: string,
    displayVerticalProgressBar?: string,
    height?: string,
    width?: string,
    orderOfEvents?: string,
    displayHeader?: string,
    displayFooter?: string,
    displayDedupeEvents?: string,
    displayRefreshStatus?: string,
    compactMode?: string;
    eventCodeFont?: string;
    eventDescriptionFont?: string;
    eventTimeFont?: string;
    supplyChainTrackingEvents?: Map<string, ClientEventInput>
    locale?: string;
    identifierType?: typeof VALID_IDENTIFIER_TYPES[number];
    identifierValue?: string;
    isGrantless?: string;
}

/** Supply chain tracking event data structure */
export interface SupplyChainTrackingEvent{
    eventCode: string
    eventShortDescription: string,
    eventTime: string,
    entityIdentifiers?:{
        containerId: string,
        trackingId: string,
        amazonReferenceNumber: string,
        bolNumber: string,
        proNumber: string,
        serviceProviderResolverString: string,
        serviceProviderResolver: string
    },
    eventAddress: {
        city?: string,
        state?: string,
        country?: string
    },
    eventDescription: {
        rttvEventDescriptionTranslatorKey?: string
    },
    estimatedDeliveryDate?: {
        startTime: string,
        endTime: string
    }
}

/** Standard tracking event format */
export interface TrackingEvent{
    eventAddress: {
        city?: string,
        state?: string,
        country?: string
    },
    eventCode: string,
    eventShortDescription: string,
    eventTimeEpoch: number,
    eventDescription: {
        rttvEventDescriptionTranslatorKey: string
    }
}

/** API response model for tracking details */
export interface ApiGetTrackingDetailsModel{
    trackingRequest: {
        trackingRequestId: string
    }
    trackingResponse: {
        currentStatus: {
            estimatedDeliveryDate: {
                endTimeEpoch?: number
            }
        },
        trackingEventList: Array<TrackingEvent>,
    }
    trackingResponseAvailable?: boolean
}

/** Current status information for supply chain tracking */
export interface SupplyChainCurrentStatus{
    estimatedDeliveryDate?: {
        startTime: string,
        endTime: string
    },
    trackingEstimates?: Array<TrackingEstimate>
}

/** Supply chain tracking response format */
export interface TrackingResponseForSupplyChain{
    trackingRequest: {
        trackingRequestId: string
    },
    trackingResponseAvailable: boolean,
    trackingResponse: {
        trackingEventList: Array<SupplyChainTrackingEvent>,
        currentStatus: SupplyChainCurrentStatus
    }
}

/** API response model for supply chain tracking details */
export interface ApiGetTrackingDetailsForSupplyChainModel{
    trackingResponseList: Array<TrackingResponseForSupplyChain>
}

/** Model for mapping client input keys to internal keys */
export interface ClientInputKeyModel{
    key: string,
    clientKey: string,
    optional?: boolean,
    defaultValue?: string,
    validationFunction?: (clientInputField: string) => boolean;
}

/** Display configuration options for the progress tracker component */
export interface ProgressTrackerDisplayOptions {
    designMode?: DesignMode,
    displayHorizontalProgressBar?: boolean,
    displayVerticalProgressBar?: boolean,
    height?: string,
    width?: string,
    orderOfEvents?: EventOrder,
    displayHeader?: boolean,
    displayFooter?: boolean,
    displayDedupeEvents?: boolean,
    displayRefreshStatus?: boolean
}
